import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import { PushPin, Event, ImportExport, Block, Delete, Replay, KeyboardBackspace, Archive, Unarchive, Download } from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import ScheduleModal from "../ScheduleModal";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);

	const [confirmationOpenBlock, setConfirmationOpenBlock] = useState(false);
	const navigate = useNavigate();
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
	const [contactId, setContactId] = useState(null);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleBlockTicket = async () => {
		try {
			await api.post(`/tickets/block/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleToggleArchiveTicket = async () => {
		try {
			await api.put(`/tickets/${ticket.id}/archive`, { archived: !ticket.archived });
			if (!ticket.archived) {
				setCurrentTicket({ id: null, code: null });
				navigate("/tickets");
			}
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenConfirmationBlockModal = e => {
		setConfirmationOpenBlock(true);
		handleClose();
	};



	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleOpenScheduleModal = () => {
		handleClose();
		setContactId(ticket.contact.id);
		setScheduleModalOpen(true);
	}

	const handleCloseScheduleModal = () => {
		setScheduleModalOpen(false);
		setContactId(null);
	}


	const handleExportConversation = async () => {
		handleClose();
		try {
			const { data, headers } = await api.get(
				`/messages/${ticket.id}/export`,
				{ responseType: "blob" }
			);

			const disposition = headers["content-disposition"] || "";
			const match = disposition.match(/filename="?([^"]+)"?/);
			const filename = match ? match[1] : `conversacion-ticket${ticket.id}.txt`;

			const url = window.URL.createObjectURL(
				new Blob([data], { type: "text/plain;charset=utf-8" })
			);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateTicket = async (e, status, userId) => {

		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? null : ticket.promptId,
				integrationId: status === "closed" ? null : ticket.integrationId
			});

			if (status === "open") {
				setCurrentTicket({ ...ticket, code: "#open" });
			} else {
				setCurrentTicket({ id: null, code: null })
				navigate("/tickets");
			}
		} catch (err) {
			toastError(err);
		}
	}


	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>


				<MenuItem onClick={e => handleUpdateTicket(e, "favorite", user?.id)}>
					<ListItemIcon>
						<PushPin />
					</ListItemIcon>
					Agregar a Favorito
				</MenuItem>
				<MenuItem onClick={e => handleUpdateTicket(e, "pending", null)}>
					<ListItemIcon>
						<Replay />
					</ListItemIcon>
					Devolver a Pendientes

				</MenuItem>
				<MenuItem onClick={handleToggleArchiveTicket}>
					<ListItemIcon>
						{ticket.archived ? <Unarchive /> : <Archive />}
					</ListItemIcon>
					{ticket.archived ? "Desarchivar" : "Archivar"}
				</MenuItem>
				<MenuItem onClick={handleOpenScheduleModal}>
					<ListItemIcon>
						<Event />
					</ListItemIcon>
					{i18n.t("ticketOptionsMenu.schedule")}
				</MenuItem>
				<MenuItem onClick={handleOpenTransferModal}>
					<ListItemIcon>
						<ImportExport />
					</ListItemIcon>
					{i18n.t("ticketOptionsMenu.transfer")}
				</MenuItem>

				<Can
					role={user.profile}
					perform="ticket-options:exportConversation"
					yes={() => (
						<MenuItem onClick={handleExportConversation}>
							<ListItemIcon>
								<Download />
							</ListItemIcon>
							Exportar Conversación
						</MenuItem>
					)}
				/>
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<>
							<MenuItem onClick={handleOpenConfirmationBlockModal}>
								<ListItemIcon>
									<Block />
								</ListItemIcon>
								{i18n.t("ticketOptionsMenu.block")}
							</MenuItem>
							<MenuItem onClick={handleOpenConfirmationModal}>
								<ListItemIcon>
									<Delete />
								</ListItemIcon>
								{i18n.t("ticketOptionsMenu.delete")}
							</MenuItem>
						</>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModalBlock.title")}${ticket.id
					} ${i18n.t("ticketOptionsMenu.confirmationModalBlock.titleFrom")} ${ticket.contact.name
					}?`}
				open={confirmationOpenBlock}
				onClose={setConfirmationOpenBlock}
				onConfirm={handleBlockTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModalBlock.message")}
			</ConfirmationModal>

			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${ticket.id
					} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${ticket.contact.name
					}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModalCustom
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
			/>
			<ScheduleModal
				open={scheduleModalOpen}
				onClose={handleCloseScheduleModal}
				aria-labelledby="form-dialog-title"
				contactId={contactId}
			/>
		</>
	);
};

export default TicketOptionsMenu;